import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select, { type StylesConfig } from 'react-select';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';
import * as XLSX from 'xlsx';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReportService from '@services/ReportService';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/Toast';
import CorgeeModal from '@/components/Globals/CorgeeModal';
import './AttendanceReport.css';

interface SelectOption {
  value: number;
  label: string;
}

interface AttendanceDetailRow {
  personId: number;
  attendanceId: number;
  employee: string;
  team: string;
  attendanceDate: string;
  clockIn: string;
  clockOut: string;
  commentClockIn: string;
  commentClockOut: string;
}

interface AttendanceSummaryRow {
  personId: number;
  employee: string;
  team: string;
  delays: number;
  missedDays: number;
}

type TableRow = AttendanceDetailRow | AttendanceSummaryRow;
type SortDir = 'asc' | 'desc';

const FILTER_OPTIONS: SelectOption[] = [
  { value: 1, label: 'Delay' },
  { value: 2, label: 'Missed Days' },
];

function getTimeZoneOffset(): string
{
  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours < 0 ? '+' : '-';
  return `${sign}${Math.abs(Math.floor(offsetHours))}`;
}

function formatDateStr(dateString: string | null | undefined): string
{
  if (!dateString || dateString === '0001-01-01T00:00:00') return '';
  const d = dateString.includes('Z') ? dateString.replace('Z', '') : dateString;
  const date = new Date(d);
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}/${day}/${y}`;
}

function formatTimeStr(dateString: string | null | undefined): string
{
  if (!dateString || dateString === '0001-01-01T00:00:00') return '';
  const d = dateString.includes('Z') ? dateString.replace('Z', '') : dateString;
  const date = new Date(d);
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${h}:${min}:${s}`;
}

// ── Custom date input (same style as filter inputs) ──────────────────────────
interface ArDateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
}
const ArDateInput = React.forwardRef<HTMLInputElement, ArDateInputProps>(
  ({ value, onClick, onClear }, ref) => (
    <div className="ar-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="ar-date-input"
        placeholder="MM/DD/YYYY"
        onChange={() => {}}
      />
      <button
        type="button"
        className="ar-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
ArDateInput.displayName = 'ArDateInput';

const SortIcon: React.FC<{ col: string; sorted: string; dir: SortDir }> = ({
  col,
  sorted,
  dir,
}) => (
  <i
    className={
      sorted === col ? (dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

const AttendanceReport: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildReportSelectStyles(isDark);
  const { PTOApprover } = useAuth();
  const { error: toastError } = useToast();

  const [hoveredAttendanceId, setHoveredAttendanceId] = useState<number | null>(null);
  const [editingComment, setEditingComment] = useState<{ attendanceId: number; value: string } | null>(null);

  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectOption[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<SelectOption | null>(null);
  const [comments, setComments] = useState('');
  const [employeeIds, setEmployeeIds] = useState('');
  const [fromDate, setFromDate] = useState(moment().startOf('isoWeek').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().endOf('isoWeek').format('YYYY-MM-DD'));
  const [showAttendanceGroup, setShowAttendanceGroup] = useState(false);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [showDateRangeMsg, setShowDateRangeMsg] = useState(false);
  const dateRangeMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loading, setLoading] = useState(false);
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    ReportService.GetAllTeams()
      .then((data: any[]) =>
      {
        setTeamOptions((data || []).map(t => ({ value: t.BasicFieldValueId, label: t.Value })));
      })
      .catch(() => {});

    ReportService.GetAllAsignedTo({
      Teams: '',
      GetTeamId: false,
      GetDepartmentId: false,
      GetPositionId: false,
      GetAllPeople: false,
    })
      .then(data =>
      {
        setEmployeeOptions((data || []).map(e => ({ value: e.PersonID ?? 0, label: e.Name })));
      })
      .catch(() => {});
  }, []);

  const buildRequest = useCallback(
    (groupOverride?: boolean) => ({
      Teams: selectedTeams.map(t => t.value).join(','),
      AsignedTo: selectedEmployees.map(e => e.value).join(','),
      DateTime: fromDate ? moment(fromDate).format('YYYYMMDD') : '',
      Duedate: toDate ? moment(toDate).format('YYYYMMDD') : '',
      projectId: '',
      sideMenuId: '',
      layersId: '',
      dashboardsId: '',
      showGroup: true,
      showAttendanceGroup: groupOverride ?? showAttendanceGroup,
      getAll: false,
      timeZone: getTimeZoneOffset(),
      comments,
      employeeids: employeeIds,
      status: '',
      artistCategory: '',
      departments: '',
      filterId: selectedFilter?.value ?? null,
    }),
    [
      selectedTeams,
      selectedEmployees,
      fromDate,
      toDate,
      showAttendanceGroup,
      comments,
      employeeIds,
      selectedFilter,
    ],
  );

  const applyFilters = useCallback(async (groupOverride?: boolean) =>
  {
    if (fromDate && moment(fromDate).isBefore(moment().subtract(2, 'years')))
    {
      if (dateRangeMsgTimerRef.current) clearTimeout(dateRangeMsgTimerRef.current);
      setShowDateRangeMsg(true);
      dateRangeMsgTimerRef.current = setTimeout(() => setShowDateRangeMsg(false), 3000);
      return;
    }
    setLoading(true);
    setTableData([]);
    try
    {
      const req = buildRequest(groupOverride);
      const resp: any[] = await ReportService.GetAttendanceReport(req);
      if (groupOverride ?? showAttendanceGroup)
      {
        setTableData(
          (resp || []).map(
            r =>
              ({
                personId: r.personId ?? 0,
                employee: r.name ?? '',
                team: r.team ?? '',
                delays: r.delays ?? 0,
                missedDays: r.missedDays ?? 0,
              }) as AttendanceSummaryRow,
          ),
        );
      }
      else
      {
        setTableData(
          (resp || []).map(
            r =>
              ({
                personId: r.personId ?? 0,
                attendanceId: r.attendanceId ?? 0,
                employee: r.name ?? '',
                team: r.team ?? '',
                attendanceDate: formatDateStr(r.attendanceDate),
                clockIn: formatTimeStr(r.clockIn),
                clockOut: formatTimeStr(r.clockOut),
                commentClockIn: r.commentClockIn ?? '',
                commentClockOut: r.commentClockOut ?? '',
              }) as AttendanceDetailRow,
          ),
        );
      }
    }
    catch (e)
    {
      console.error('AttendanceReport error:', e);
    }
    setLoading(false);
  }, [buildRequest, showAttendanceGroup]);

  const handleSort = (col: string) =>
  {
    const dir = sortCol === col && sortDir === 'asc' ? 'desc' : 'asc';
    setSortCol(col);
    setSortDir(dir);
    setTableData(prev =>
      [...prev].sort((a, b) =>
      {
        const av = (a as any)[col] ?? '';
        const bv = (b as any)[col] ?? '';
        if (av < bv) return dir === 'asc' ? -1 : 1;
        if (av > bv) return dir === 'asc' ? 1 : -1;
        return 0;
      }),
    );
  };

  const toggleAttendanceGroup = () =>
  {
    const next = !showAttendanceGroup;
    setShowAttendanceGroup(next);
    applyFilters(next);
  };

  const handleOpenEditComment = (attendanceId: number, currentValue: string) =>
  {
    setEditingComment({ attendanceId, value: currentValue });
  };

  const handleSaveEditComment = async () =>
  {
    if (!editingComment) return;
    try
    {
      const result = await ReportService.SaveClockOutComment({
        TimeTrackingID: editingComment.attendanceId,
        comments: editingComment.value,
      });
      if (!result)
      {
        toastError('The comment for clocking out was not updated');
        return;
      }
      setTableData(prev =>
        prev.map(row =>
          (row as AttendanceDetailRow).attendanceId === editingComment.attendanceId
            ? { ...row, commentClockOut: editingComment.value }
            : row,
        ),
      );
      setEditingComment(null);
    }
    catch (error)
    {
      console.error('Error saving clock out comment:', error);
      toastError('The comment for clocking out was not updated');
    }
  };

  const exportToExcel = () =>
  {
    if (!tableData.length) return;
    let rows: Record<string, any>[];
    let headers: string[];

    if (showAttendanceGroup)
    {
      headers = ['EmployeeID', 'Employee', 'Team', 'Delays', 'Missed Days'];
      rows = tableData.map(r =>
      {
        const row = r as AttendanceSummaryRow;
        return {
          EmployeeID: row.personId,
          Employee: row.employee,
          Team: row.team,
          Delays: row.delays,
          'Missed Days': row.missedDays,
        };
      });
    }
    else
    {
      headers = [
        'EmployeeID',
        'Employee',
        'Team',
        'Attendance Date',
        'Clock In',
        'Clock Out',
        'Comment Clock In',
        'Comment Clock Out',
      ];
      rows = tableData.map(r =>
      {
        const row = r as AttendanceDetailRow;
        return {
          EmployeeID: row.personId,
          Employee: row.employee,
          Team: row.team,
          'Attendance Date': row.attendanceDate,
          'Clock In': row.clockIn,
          'Clock Out': row.clockOut,
          'Comment Clock In': row.commentClockIn,
          'Comment Clock Out': row.commentClockOut,
        };
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, 'Attendance.xlsx');
  };

  const colSpan = showAttendanceGroup ? 5 : 9;

  return (
    <>
      <div className="attendance-report" id="AttendanceReport">
        <h3 className="ar-title">Attendance Report</h3>
        <p className="ar-subtitle">
          This report contains information about the user's attendance registers
        </p>

        <div className="ar-filters">
          <div className="ar-filter-row">
            <div className="ar-filter-item">
              <label className="ar-label">Team(s):</label>
              <Select
                isMulti
                options={teamOptions}
                value={selectedTeams}
                onChange={v => setSelectedTeams(v as SelectOption[])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                className="ar-select"
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="ar-filter-item">
              <label className="ar-label">Employee(s):</label>
              <Select
                isMulti
                options={employeeOptions}
                value={selectedEmployees}
                onChange={v => setSelectedEmployees(v as SelectOption[])}
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, true>}
                className="ar-select"
                closeMenuOnSelect={false}
                components={{ ClearIndicator }}
              />
            </div>
            <div className="ar-filter-item ar-filter-item--sm">
              <label className="ar-label">Filter(s):</label>
              <Select
                options={FILTER_OPTIONS}
                value={selectedFilter}
                onChange={v => setSelectedFilter(v as SelectOption | null)}
                isClearable
                placeholder="Select..."
                styles={selectStyles as StylesConfig<SelectOption, false>}
                className="ar-select"
              />
            </div>
            <div className="ar-filter-item">
              <label className="ar-label">Comment(s):</label>
              <input
                type="text"
                className="ar-text-input"
                placeholder="Write here..."
                value={comments}
                onChange={e => setComments(e.target.value)}
              />
            </div>
            <div className="ar-filter-item">
              <label className="ar-label">EmployeeID(s):</label>
              <input
                type="text"
                className="ar-text-input"
                placeholder="Write here..."
                value={employeeIds}
                onChange={e => setEmployeeIds(e.target.value)}
              />
            </div>
            {/* dates and checkbox continue in the same grid */}
            <div className="ar-filter-item ar-filter-item--date">
              <label className="ar-label">From:</label>
              <DatePicker
                selected={fromDate ? moment(fromDate, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: any) => setFromDate(d ? moment(d).format('YYYY-MM-DD') : '')}
                maxDate={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)}
                dateFormat="MM/dd/yyyy"
                customInput={<ArDateInput onClear={() => setFromDate('')} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="ar-datepicker-calendar"
              />
            </div>
            <div className="ar-filter-item ar-filter-item--date">
              <label className="ar-label">To:</label>
              <DatePicker
                selected={toDate ? moment(toDate, 'YYYY-MM-DD').toDate() : null}
                onChange={(d: any) => setToDate(d ? moment(d).format('YYYY-MM-DD') : '')}
                maxDate={new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)}
                dateFormat="MM/dd/yyyy"
                customInput={<ArDateInput onClear={() => setToDate('')} />}
                popperProps={{ strategy: 'fixed' }}
                showPopperArrow={false}
                calendarClassName="ar-datepicker-calendar"
              />
            </div>
            <div className="ar-filter-item--btn">
              <span className="ar-label-spacer" aria-hidden="true">
                &nbsp;
              </span>
              <label className="ar-checkbox-label">
                <input
                  type="checkbox"
                  checked={showAttendanceGroup}
                  onChange={toggleAttendanceGroup}
                />
                <span>Show Delays/Missed Days</span>
              </label>
            </div>
            {/* Apply starts a new grid row, matching the first combo width */}
            <div className="ar-filter-item ar-filter-item--apply">
              <button className="ar-apply-btn" onClick={() => applyFilters()} disabled={loading}>
                {loading ? 'Loading...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>

        <div className="ar-table-wrapper">
          <div className="ar-export-row">
            <button
              className="ar-export-btn"
              onClick={exportToExcel}
              title="Export to Excel"
              disabled={!tableData.length}
            >
              <span className="material-icons style-icon-cloud">cloud_download</span> Export
            </button>
          </div>
          <div className="ar-table-scroll">
            <table className={`ar-table ${showAttendanceGroup ? 'ar-table-group' : 'ar-table-detail'}`}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('personId')}>
                    EmployeeID <SortIcon col="personId" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('employee')}>
                    Employees <SortIcon col="employee" sorted={sortCol} dir={sortDir} />
                  </th>
                  <th onClick={() => handleSort('team')}>
                    Team <SortIcon col="team" sorted={sortCol} dir={sortDir} />
                  </th>
                  {showAttendanceGroup ? (
                    <>
                      <th onClick={() => handleSort('delays')}>
                        Delays <SortIcon col="delays" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('missedDays')}>
                        Missed Days <SortIcon col="missedDays" sorted={sortCol} dir={sortDir} />
                      </th>
                    </>
                  ) : (
                    <>
                      <th onClick={() => handleSort('attendanceDate')}>
                        Attendance date{' '}
                        <SortIcon col="attendanceDate" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('clockIn')}>
                        Clock In <SortIcon col="clockIn" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('clockOut')}>
                        Clock Out <SortIcon col="clockOut" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('commentClockIn')}>
                        Clock In Comment{' '}
                        <SortIcon col="commentClockIn" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('commentClockOut')}>
                        Clock Out Comment{' '}
                        <SortIcon col="commentClockOut" sorted={sortCol} dir={sortDir} />
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, i) =>
                {
                  if (showAttendanceGroup)
                  {
                    const r = row as AttendanceSummaryRow;
                    return (
                      <tr key={i}>
                        <td>{r.personId}</td>
                        <td>{r.employee}</td>
                        <td>{r.team}</td>
                        <td>{r.delays}</td>
                        <td>{r.missedDays}</td>
                      </tr>
                    );
                  }
                  const r = row as AttendanceDetailRow;
                  return (
                    <tr key={i}>
                      <td>{r.personId}</td>
                      <td>{r.employee}</td>
                      <td>{r.team}</td>
                      <td>{r.attendanceDate}</td>
                      <td>{r.clockIn}</td>
                      <td>{r.clockOut}</td>
                      <td>{r.commentClockIn}</td>
                      <td
                        className="ar-comment-cell"
                        onMouseEnter={() => setHoveredAttendanceId(r.attendanceId)}
                        onMouseLeave={() => setHoveredAttendanceId(null)}
                      >
                        {r.commentClockOut}
                        {PTOApprover() &&
                          r.commentClockOut &&
                          hoveredAttendanceId === r.attendanceId && (
                          <i
                            className="fa fa-pencil ar-edit-comment-icon"
                            title="Edit Clock Out Comment"
                            onClick={() => handleOpenEditComment(r.attendanceId, r.commentClockOut)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loading && tableData.length === 0 && (
                  <tr>
                    <td colSpan={colSpan} className="ar-no-data">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showDateRangeMsg &&
        createPortal(
          <>
            <style>{`
            @keyframes ar-date-msg {
              0%   { opacity: 0; }
              10%  { opacity: 1; }
              90%  { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
            <div
              style={{
                position: 'fixed',
                top: '64px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#62abcb',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                pointerEvents: 'none',
                zIndex: 9999,
                whiteSpace: 'nowrap',
                animation: 'ar-date-msg 3s ease forwards',
              }}
            >
              The start date must be within the last 2 years
            </div>
          </>,
          document.body,
        )}

      <CorgeeModal
        openModal={!!editingComment}
        title="Attendance"
        onClose={() => setEditingComment(null)}
        onSave={handleSaveEditComment}
        textButton="Save"
        width={315}
        className="ar-edit-comment-modal"
        transparentBackdrop
        saveButtonColor={editingComment?.value.trim() ? '#F26C4F' : ''}
      >
        <div className="ar-edit-comment-field">
          <label className="ar-edit-comment-label">Comment:</label>
          <input
            type="text"
            className="ar-edit-comment-input"
            value={editingComment?.value ?? ''}
            onChange={e =>
              setEditingComment(prev => (prev ? { ...prev, value: e.target.value } : prev))
            }
          />
        </div>
      </CorgeeModal>
    </>
  );
};

export default AttendanceReport;
